import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const processSteps = [
  {
    id: "01",
    title: "Technical Consultation & Substrate Mapping",
    tag: "Consultation",
    description:
      "We analyze your application environment—moisture, temperature, and surface texture—to map the ideal material and adhesive combination for performance and cost.",
    points: [
      "Application-specific adhesive selection",
      "Material durability and cost-benefit review",
      "Technical feasibility check for labels",
    ],
  },
  {
    id: "02",
    title: "Pre-Press & Color Standardization",
    tag: "Pre-Production",
    description:
      "Artwork is optimized for high-fidelity output. We manage G7 color standards and trapping to ensure design integrity remains intact through production.",
    points: [
      "G7 master color matching protocols",
      "Print-ready file optimization & trapping",
      "Digital soft-proofing for accuracy",
    ],
  },
  {
    id: "03",
    title: "Precision Flexo & Digital Production",
    tag: "Manufacturing",
    description:
      "Executing production on high-speed lines with real-time registration control. We manage ink density and finishing inline for repeatable quality.",
    points: [
      "High-speed Flexo & Digital execution",
      "Inline lamination and metallic finishes",
      "Controlled ink-to-substrate registration",
    ],
  },
  {
    id: "04",
    title: "100% Quality Inspection & QC Release",
    tag: "Quality Control",
    description:
      "Every reel passes through structured quality checks including rub tests, barcode scannability, and visual inspection before release.",
    points: [
      "Automated optical scan verification",
      "Adhesion and rub-test validation tests",
      "ANSI barcode grading & compliance",
    ],
  },
  {
    id: "05",
    title: "Lean Packing & JIT Dispatch",
    tag: "Logistics",
    description:
      "Labels are custom-rewound to your applicator specs and packed in protective industrial crates for safe, just-in-time delivery.",
    points: [
      "Custom core and rewind directions",
      "Crush-proof industrial packaging",
      "Coordinated JIT delivery logistics",
    ],
  },
];

const quickStats = [
  { value: "G7", label: "Certified Color Matching" },
  { value: "ANSI", label: "Barcode Grade Compliance" },
  { value: "ISO", label: "Production Discipline" },
];

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7 },
};

const Sample = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#0a0f1a] text-white">
      {/* Hero Section - Centered & Professional */}
      <section className="relative overflow-hidden pt-[160px] pb-24 md:pt-[200px] md:pb-32 lg:pt-[220px]">
        {/* Cleaner Gradient Background */}
        <div className="absolute inset-0 bg-[#0a0f1a]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,0.12),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:40px_40px] opacity-40" />

        <div className="relative z-10 mx-auto max-w-[1200px] px-6">
          <div className="flex flex-col items-center text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-[950px]"
            >
              <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/5 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
                Workflow Architecture
              </div>

              <h1 className="mb-8 text-[44px] font-extrabold leading-[1.05] tracking-tight text-white md:text-[68px] lg:text-[84px]">
                Engineering Reliability
                <span className="block bg-gradient-to-b from-white to-white/40 bg-clip-text text-transparent">
                  Into Every Single Reel.
                </span>
              </h1>

              <p className="mx-auto mb-12 max-w-[700px] text-lg leading-relaxed text-white/50 md:text-xl">
                Sahajink operates a production-first manufacturing workflow
                designed exclusively for industrial reliability and
                color-critical labeling.
              </p>

              <div className="flex flex-wrap justify-center gap-6">
                <Link
                  to="/#contact"
                  className="rounded-full bg-blue-600 px-10 py-5 text-sm font-bold uppercase tracking-widest text-white transition-all hover:bg-blue-500"
                >
                  Discuss Requirement
                </Link>
                <Link
                  to="/our-works"
                  className="rounded-full border border-white/10 bg-white/5 px-10 py-5 text-sm font-bold uppercase tracking-widest text-white transition-all hover:bg-white/10"
                >
                  View Work
                </Link>
              </div>
            </motion.div>

            {/* Metrics Row */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-28 w-full border-t border-white/5 pt-12"
            >
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
                {quickStats.map((item, index) => (
                  <div key={item.label} className="flex flex-col items-center">
                    <div className="text-4xl font-black text-white">
                      {item.value}
                    </div>
                    <div className="mt-3 text-[10px] font-black uppercase tracking-[0.3em] text-white/30">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Process Flow - Removed redundant sections, focused on industrial hierarchy */}
      <section className="relative bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1100px] px-6">
          <motion.div {...fadeUp} className="mb-20 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-950 md:text-5xl">
              Manufacturing Stage Breakdown
            </h2>
            <div className="mx-auto mt-6 h-1 w-20 bg-blue-600 rounded-full" />
          </motion.div>

          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="grid gap-8 overflow-hidden rounded-[32px] border border-slate-100 bg-slate-50/40 p-8 md:grid-cols-[1fr_1.1fr] md:p-12 transition-all hover:border-blue-100 hover:bg-white hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.06)]"
              >
                <div>
                  <div className="mb-6 flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-lg font-black text-white">
                      {step.id}
                    </span>
                    <span className="text-[11px] font-black uppercase tracking-widest text-blue-600">
                      {step.tag}
                    </span>
                  </div>
                  <h3 className="text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
                    {step.title}
                  </h3>
                  <p className="mt-5 text-base leading-relaxed text-slate-600">
                    {step.description}
                  </p>
                </div>

                <div className="rounded-3xl border border-slate-200/50 bg-white p-8">
                  <p className="mb-6 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
                    Process Benchmarks
                  </p>
                  <div className="space-y-4">
                    {step.points.map((point) => (
                      <div key={point} className="flex items-start gap-4">
                        <div className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                        <p className="text-sm font-semibold leading-relaxed text-slate-700">
                          {point}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-white/5 bg-[#0a0f1a] py-24 md:py-32">
        <div className="mx-auto max-w-[850px] px-6 text-center">
          <motion.div {...fadeUp}>
            <h2 className="mb-8 text-4xl font-extrabold tracking-tight text-white md:text-6xl">
              Reliability as a Service.
            </h2>
            <p className="mb-12 text-lg text-white/40 leading-relaxed md:text-xl">
              We translate technical requirements into controlled production
              output. Let's start mapping your next label requirement.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link
                to="/#contact"
                className="rounded-full bg-white px-10 py-5 text-sm font-bold uppercase tracking-widest text-[#0a0f1a] transition-all hover:bg-blue-100"
              >
                Discuss Requirement
              </Link>
              <Link
                to="/"
                className="text-sm font-bold uppercase tracking-widest text-white/40 transition-all hover:text-white"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Sample;
